import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { ChevronLeft, ChevronRight, RefreshCw, Star, Trophy, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ==================== ADMIN CALENDAR ====================

export function AdminCalendarTab() {
  const [schedules, setSchedules] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/schedule/admin`, { headers: getAuthHeaders() }),
      axios.get(`${API}/admin/engagements/calendar`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
    ]).then(([sRes, eRes]) => {
      setSchedules(sRes.data);
      setEngagements(eRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const year = month.getFullYear();
  const mo = month.getMonth();
  const firstDay = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const monthName = month.toLocaleString('default', { month: 'long', year: 'numeric' });

  const schedulesByDate = useMemo(() => {
    const map = {};
    schedules.forEach(s => {
      const d = s.scheduled_date;
      if (!d) return;
      if (!map[d]) map[d] = [];
      map[d].push({ ...s, item_type: 'post' });
    });
    engagements.forEach(e => {
      const d = e.event_date;
      if (!d) return;
      if (!map[d]) map[d] = [];
      map[d].push({ ...e, item_type: 'engagement', executive_name: e.brand_name, campaign_title: e.title, has_post: false, missed: false, submission_status: e.status });
    });
    return map;
  }, [schedules, engagements]);

  const getDayStatus = (dateStr) => {
    const items = schedulesByDate[dateStr] || [];
    if (items.length === 0) return null;
    const hasEngagement = items.some(i => i.item_type === 'engagement');
    const hasMissed = items.some(i => i.missed);
    const hasSubmitted = items.some(i => i.has_post || ['submitted', 'verified', 'approved', 'paid', 'completed'].includes(i.submission_status));
    if (hasEngagement && hasSubmitted) return 'both';
    if (hasEngagement) return 'engagement';
    if (hasMissed) return 'missed';
    if (hasSubmitted) return 'submitted';
    return 'scheduled';
  };

  const statusColors = { scheduled: 'bg-[#6B7280]', submitted: 'bg-[#10B981]', missed: 'bg-[#FF4343]', engagement: 'bg-blue-400', both: 'bg-blue-400' };

  const prevMonth = () => setMonth(new Date(year, mo - 1, 1));
  const nextMonth = () => setMonth(new Date(year, mo + 1, 1));

  const dayItems = selectedDay ? (schedulesByDate[selectedDay] || []) : [];

  return (
    <div data-testid="admin-calendar-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-sm font-semibold">Content Calendar</h3>
        <div className="flex items-center gap-4">
          <div className="flex gap-3 text-[9px]">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#6B7280] rounded-full" /> Scheduled</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#10B981] rounded-full" /> Submitted</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#FF4343] rounded-full" /> Missed</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-blue-400 rounded-full" /> Engagement</span>
          </div>
          {/* Cron buttons */}
          <div className="flex gap-1">
            <button onClick={async () => {
              try { const { data } = await axios.post(`${API}/cron/reminders`, {}, { headers: getAuthHeaders() }); alert(`Sent: ${data.sent_48h} (48h) + ${data.sent_24h} (24h)`); } catch {}
            }} className="text-[9px] px-2 py-1 border border-white/10 text-[#6B7280] hover:text-white" data-testid="run-reminders-btn">Run Reminders</button>
            <button onClick={async () => {
              try { const { data } = await axios.post(`${API}/cron/missed-deadlines`, {}, { headers: getAuthHeaders() }); alert(`Found ${data.missed_found} missed, notified ${data.notified}`); } catch {}
            }} className="text-[9px] px-2 py-1 border border-white/10 text-[#6B7280] hover:text-white" data-testid="run-missed-btn">Check Missed</button>
            <button onClick={async () => {
              try { const { data } = await axios.post(`${API}/cron/auto-approve`, {}, { headers: getAuthHeaders() }); alert(`Auto-approved: ${data.auto_approved}`); } catch {}
            }} className="text-[9px] px-2 py-1 border border-white/10 text-[#6B7280] hover:text-white" data-testid="run-auto-approve-btn">Auto-Approve</button>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Month Grid */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="text-[#6B7280] hover:text-white p-1"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-semibold">{monthName}</span>
            <button onClick={nextMonth} className="text-[#6B7280] hover:text-white p-1"><ChevronRight className="w-4 h-4" /></button>
          </div>
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-px mb-1">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <div key={d} className="text-center text-[9px] font-mono text-[#6B7280] uppercase py-1">{d}</div>
            ))}
          </div>
          {/* Day cells */}
          <div className="grid grid-cols-7 gap-px" data-testid="calendar-grid">
            {Array(firstDay).fill(null).map((_, i) => <div key={`empty-${i}`} className="h-16 bg-[#111827]/30" />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(mo + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const status = getDayStatus(dateStr);
              const isSelected = selectedDay === dateStr;
              const isToday = dateStr === new Date().toISOString().split('T')[0];
              const count = (schedulesByDate[dateStr] || []).length;

              return (
                <div key={day} onClick={() => setSelectedDay(isSelected ? null : dateStr)}
                  className={`h-16 bg-[#111827] border cursor-pointer transition-colors relative p-1.5
                    ${isSelected ? 'border-[#FF4343]' : isToday ? 'border-white/20' : 'border-white/5 hover:border-white/10'}`}
                  data-testid={`cal-day-${dateStr}`}>
                  <span className={`text-[10px] ${isToday ? 'text-[#FF4343] font-bold' : 'text-[#6B7280]'}`}>{day}</span>
                  {status && (
                    <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                      {status === 'both' ? (
                        <><span className="w-2 h-2 rounded-full bg-[#10B981]" /><span className="w-2 h-2 rounded-full bg-blue-400" /></>
                      ) : (
                        <span className={`w-2 h-2 rounded-full ${statusColors[status]}`} />
                      )}
                      {count > 1 && <span className="text-[8px] text-[#6B7280]">+{count - 1}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Day Detail Sidebar */}
        <div className="w-72 flex-shrink-0">
          {selectedDay ? (
            <div className="bg-[#111827] border border-white/10 p-4">
              <h4 className="text-xs font-semibold mb-3">{new Date(selectedDay + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</h4>
              {dayItems.length === 0 ? (
                <p className="text-[10px] text-[#6B7280]">No posts scheduled for this day.</p>
              ) : (
                <div className="space-y-2">
                  {dayItems.map(item => (
                    <div key={item.id} className={`p-3 border text-xs ${
                      item.item_type === 'engagement' ? 'border-blue-400/30 bg-blue-400/5' :
                      item.missed ? 'border-[#FF4343]/30 bg-[#FF4343]/5' :
                      item.has_post ? 'border-[#10B981]/30 bg-[#10B981]/5' :
                      'border-white/10'
                    }`}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        {item.item_type === 'engagement' && <span className="text-[8px] font-mono text-blue-400 bg-blue-400/10 px-1 py-0.5">ENG</span>}
                        <p className="text-white font-medium">{item.executive_name || item.title}</p>
                      </div>
                      <p className="text-[#6B7280] text-[10px]">{item.campaign_title || ''}</p>
                      <span className={`text-[9px] font-mono uppercase mt-1 inline-block ${
                        item.item_type === 'engagement' ? 'text-blue-400' :
                        item.missed ? 'text-[#FF4343]' : item.has_post ? 'text-[#10B981]' : 'text-[#6B7280]'
                      }`}>
                        {item.item_type === 'engagement' ? (item.engagement_subtype || item.engagement_type || 'ENGAGEMENT').replace(/_/g,' ') :
                         item.missed ? 'MISSED' : item.has_post ? 'SUBMITTED' : 'SCHEDULED'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-[#111827] border border-white/10 p-4 text-center">
              <p className="text-[10px] text-[#6B7280]">Click a day to see scheduled posts</p>
            </div>
          )}

          {/* Summary */}
          <div className="mt-4 grid grid-cols-4 gap-2">
            <div className="bg-[#111827] border border-white/10 p-3 text-center">
              <span className="font-mono text-lg font-bold text-white">{schedules.length}</span>
              <p className="text-[8px] text-[#6B7280] font-mono uppercase">Posts</p>
            </div>
            <div className="bg-[#111827] border border-white/10 p-3 text-center">
              <span className="font-mono text-lg font-bold text-[#10B981]">{schedules.filter(s => s.has_post).length}</span>
              <p className="text-[8px] text-[#6B7280] font-mono uppercase">Done</p>
            </div>
            <div className="bg-[#111827] border border-white/10 p-3 text-center">
              <span className="font-mono text-lg font-bold text-[#FF4343]">{schedules.filter(s => s.missed).length}</span>
              <p className="text-[8px] text-[#6B7280] font-mono uppercase">Missed</p>
            </div>
            <div className="bg-[#111827] border border-white/10 p-3 text-center">
              <span className="font-mono text-lg font-bold text-blue-400">{engagements.length}</span>
              <p className="text-[8px] text-[#6B7280] font-mono uppercase">Events</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== REFERRAL LEADERBOARD ====================

function TierBadge({ tier }) {
  if (tier === 'elite') return <span className="flex items-center gap-0.5 text-[9px] font-mono text-[#FF4343]"><Star className="w-3 h-3 fill-[#FF4343]" /></span>;
  if (tier === 'verified') return <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />;
  return <span className="w-2 h-2 rounded-full bg-[#6B7280] inline-block" />;
}

export function ReferralLeaderboardTab() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/referral-leaderboard`, { headers: getAuthHeaders() })
      .then(r => setLeaders(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="referral-leaderboard-tab">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#FF4343]" />
          <h3 className="text-sm font-semibold">Referral Leaderboard</h3>
        </div>
      </div>

      {loading ? <p className="text-[#9CA3AF]">Loading...</p> : leaders.length === 0 ? (
        <div className="text-center py-12 bg-[#111827] border border-white/10">
          <Users className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#6B7280] text-sm">No referrals yet. Leaders will appear here as executives share their referral links.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {leaders.map((l, i) => (
            <div key={l.referrer_id} className={`bg-[#111827] border p-4 flex items-center gap-4 ${
              i === 0 ? 'border-[#FF4343]/30 border-l-[3px] border-l-[#FF4343]' : i < 3 ? 'border-white/20' : 'border-white/10'
            }`} data-testid={`leader-${i}`}>
              {/* Rank */}
              <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${
                i === 0 ? 'bg-[#FF4343] text-[#090E33]' : i < 3 ? 'bg-white/10 text-white' : 'bg-white/5 text-[#6B7280]'
              }`}>
                <span className="font-mono text-sm font-bold">{i + 1}</span>
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white font-medium">{l.name}</span>
                  <TierBadge tier={l.tier} />
                </div>
                <span className="text-[10px] text-[#6B7280]">{l.company}</span>
              </div>
              {/* Stats */}
              <div className="flex gap-4 items-center flex-shrink-0">
                <div className="text-center">
                  <span className="font-mono text-lg font-bold text-white">{l.total_referrals}</span>
                  <p className="text-[8px] text-[#6B7280] font-mono uppercase">Referred</p>
                </div>
                <div className="text-center">
                  <span className="font-mono text-lg font-bold text-[#10B981]">{l.qualified}</span>
                  <p className="text-[8px] text-[#6B7280] font-mono uppercase">Qualified</p>
                </div>
                <div className="text-center">
                  <span className="font-mono text-lg font-bold text-[#FF4343]">${l.total_earned.toLocaleString()}</span>
                  <p className="text-[8px] text-[#6B7280] font-mono uppercase">Earned</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
